import { Injectable, EventEmitter } from '@angular/core';
import { BhtpPurchaseService, ApiSmartQuoteResponse, ApiQuoteTripResponse } from '@bhtp-client-api/purchase';
import {
    SessionStorage, LocalStorage, ApiQuoteRequest, ApiQuoteResponse, ApiResponseContainer, ApiCoverage,
    ApiPolicy, ApiTraveler, ApiAddress, ApiEmailQuote, ApiFlight, ApiPaymentInformation, ApiPackageRequest,
    ApiCoverageDetail
} from '@bhtp-client-api/base';
import { AcknowledgementService, AirlineViewModel } from '@bhtp-client-web/component';
import { PurchaseData, Traveler, EmailQuote, AgentProfile, Flight, BillingInformation, Address, Coverage, EmailGroup } from './../shared';
import { Observable } from 'rxjs';
import { Constants } from './../shared/settings/constants';

import * as moment from 'moment';

@Injectable()
export class QuotingService {
    public quoteCompleted: EventEmitter<ApiQuoteResponse> = new EventEmitter<ApiQuoteResponse>();
    public purchaseDataUpdated: EventEmitter<PurchaseData> = new EventEmitter<PurchaseData>();

    // Hard coded for now
    private merchantProfile = 'BHTP';
    private paymentMethod = 'Compliant';

    constructor(private purchaseService: BhtpPurchaseService, private sessionStorage: SessionStorage, private localStorage: LocalStorage) { }

    public getQuote(
        purchaseData: PurchaseData,
        saveQuote: boolean,
        flightCount: number = null,
        emitResult: boolean = true,
        onlyQuoteSelectedCoverages: boolean = false): Observable<ApiQuoteResponse> {
        let request: ApiQuoteRequest = this.mapQuoteRequest(purchaseData, flightCount);
        if (onlyQuoteSelectedCoverages) {
            request.coverages = this.getSelectedCoverages(purchaseData, false);
        }

        const getQuote: Observable<ApiQuoteResponse> = this.purchaseService.getQuote(request)
            .map(result => {
                if (result && result.response) {
                    return result.response;
                }

                return null;
            })
            .share()
            .catch((error: any) => Observable.throw(error.messages));

        getQuote.subscribe(response => {
            if (response) {
                this.reconcileQuoteResponse(purchaseData, response, onlyQuoteSelectedCoverages);

                if (saveQuote) {

                    // save package when storing current quote
                    if (!response.package) {

                        const currentQuote = this.getCurrentQuote();

                        if (currentQuote  && currentQuote.package) {
                                response.package = currentQuote.package;
                        }
                    }

                    this.storeCurrentQuote(response);
                }

                if (emitResult) {
                    this.quoteCompleted.emit(response);
                }
            }
        }, err => {
            // need to handle error in service as well or else error won't bubble up to child subscribers
        });

        return getQuote;
    }

    public getQuoteByQuoteNumber(quoteNumber: string): Observable<ApiResponseContainer<ApiQuoteTripResponse>> {
        return this.purchaseService.getQuoteByNumber(quoteNumber)
            .share()
            .catch((response: any) => Observable.throw((<ApiResponseContainer<ApiQuoteTripResponse>>response.json()).messages));
    }

    public purchaseQuote(purchaseData: PurchaseData, billingInformation: BillingInformation, skipQueue: boolean = false): Observable<ApiResponseContainer<ApiQuoteResponse>> {
        let request: ApiQuoteRequest = this.mapQuoteRequest(purchaseData);
        request.paymentInformation = this.mapPaymentInformation(billingInformation);
        request.policy.isQuickQuote = false;
        request.coverages = this.getSelectedCoverages(purchaseData, true);
        request.flights = this.checkFlightsForPurchase(request);

        return this.purchaseService.purchaseQuote(request, skipQueue)
            .share()
            .catch((error: any) => Observable.throw(error.messages));
    }

    public storeCurrentQuote(quote: ApiQuoteResponse): void {
        if (quote) {
            this.sessionStorage.storeValue('currentQuote', JSON.stringify(quote));
        }
    }

    public getCurrentQuote(): ApiQuoteResponse {
        try {
            let data: string = this.sessionStorage.getValue('currentQuote');
            if (data) {
                return JSON.parse(data);
            }
        }
        catch (exception) { }

        return null;
    }

    public mapQuoteTripResponseToPurchaseData(quote: ApiQuoteTripResponse, sessionId: number): PurchaseData {
        // create and map purchase data - to be used in purchase path
        let data = new PurchaseData();
        data.sessionId = sessionId || null;
        data.quoteNumber = quote.quoteNumber;

        // assigns agency/agent code on quote load - original agent on quote could be different than agent making edits/purchase
        // the api will check facilitating agent/agency codes and move them to agent/agency code field,  so that the api will
        // recheck license and assign accordingly.

        data.agencyCode = quote.agencyCode;
        data.agentCode = quote.agentCode;

        // map purchase data for Travelers step
        this.mapPurchaseDataForTravelers(data, quote);

        // map purchase data for Coverages step
        this.mapPurchaseCoverages(data, quote);

        // map purchase data for Flights step
        this.mapPurchaseDataFlights(data, quote);

        // map purchase data for Finish Quote step
        this.mapPurchaseDataForFinishQuote(data, quote);

        return data;
    }

    private reconcileQuoteResponse(purchaseData: PurchaseData, response: ApiQuoteResponse, onlyQuoteSelectedCoverages: boolean = false): void {
        if (response.coverages) {
            let dataUpdated = false;
            if (response.coverages.optional) {
                // reconcile selected coverages to display updated premiums
                if (purchaseData && purchaseData.selectedCoverages) {
                    purchaseData.selectedCoverages.forEach((coverage: Coverage, index: number) => {
                        let quotedCoverage: ApiCoverageDetail = response.coverages.optional.find(qc => coverage.ratingId === qc.ratingId && qc.coverageAvailable);
                        if (quotedCoverage) {
                            coverage.premium = quotedCoverage.premium;
                            coverage.premiumsByDay = quotedCoverage.premiumsByDay;
                            coverage.premiumsByLimit = quotedCoverage.premiumsByLimit;

                            // set the premium for the number of days the coverage
                            // is effective
                            if (coverage.daySpan && coverage.premiumsByDay) {
                                coverage.premium = coverage.premiumsByDay[coverage.daySpan];
                            }
                        }
                    });

                    dataUpdated = true;
                }
            }

            purchaseData.availableCoverages = response.coverages;
            purchaseData.documentUrl = response.docLink;

            if (!onlyQuoteSelectedCoverages || !purchaseData.availableCoverages) {
                dataUpdated = true;
            }

            if (dataUpdated) {
                purchaseData.updateQuoteMessage = this.compareEditedQuote(response);
                this.purchaseDataUpdated.emit(purchaseData);
            }
        }
    }

    private getAgentProfile(): AgentProfile {
        try {
            let data: string = this.localStorage.getValue('profile');
            if (data) {
                return <AgentProfile>JSON.parse(data);
            }

            return null;
        }
        catch (exception) {
            return null;
        }
    }

    private getSelectedCoverages(purchaseData: PurchaseData, forPurchase: boolean = false): ApiCoverage[] {
        let coverages: ApiCoverage[] = [];
        if (purchaseData.selectedCoverages) {
            purchaseData.selectedCoverages.forEach(c => {
                if (!forPurchase || (forPurchase && purchaseData.availableCoverages.optional.some(ac => ac.ratingId === c.ratingId && ac.coverageAvailable))) {
                    let selectedCoverage: ApiCoverage = new ApiCoverage();
                    selectedCoverage.ratingId = c.ratingId;

                    if (Coverage.canAddLimitControl(c)) {
                        selectedCoverage.coverageLimit = c.coverageLimit;
                    }

                    coverages.push(selectedCoverage);
                }
            });
        }

        return coverages;
    }

    private mapQuoteRequest(purchaseData: PurchaseData, flightCount: number = null): ApiQuoteRequest {
        let request: ApiQuoteRequest = new ApiQuoteRequest();

        request.policy = new ApiPolicy();
        request.policy.agentCode = purchaseData.agentCode;
        request.policy.agencyCode = purchaseData.agencyCode;
        request.policy.quoteNumber = purchaseData.quoteNumber;
        request.policy.packageName = purchaseData.ratingId;
        request.policy.productId = purchaseData.ratingId;
        request.policy.departureDate = purchaseData.departureDate;
        request.policy.returnDate = purchaseData.returnDate;
        request.policy.tripDepositDate = purchaseData.depositDate;
        request.policy.destinationCountry = purchaseData.destinationCountry ? purchaseData.destinationCountry.isoCode2 : null;
        request.policy.destinationState = purchaseData.destinationState;
        request.policy.destinationCity = purchaseData.destinationCity;

        request.policy.airline = purchaseData.airline;
        request.policy.cruiseLine = purchaseData.cruiseLine;
        request.policy.hotel = purchaseData.hotel;
        request.policy.rentalCompany = purchaseData.rentalCompany;
        request.policy.tourOperator = purchaseData.tourOperator;

        request.policy.carRentalCompany = purchaseData.carRentalCompany;
        request.policy.rentalCarPickupDate = purchaseData.rentalCarPickupDate;
        request.policy.rentalCarReturnDate = purchaseData.rentalCarReturnDate;

        let primaryTraveler: ApiTraveler = null;
        if (purchaseData.primaryTraveler) {
            request.travelers = new Array<ApiTraveler>();
            primaryTraveler = this.mapTraveler(purchaseData.primaryTraveler);
            primaryTraveler.isPrimary = true;

            if (purchaseData.primaryTraveler.emailGroup) {
                if (purchaseData.primaryTraveler.emailGroup.noEmail) {
                    request.policy.fulfillmentMethod = 'USPS';

                    // explicitly set the email to null, or else it will be an empty string and cause errors when going into the api
                    primaryTraveler.email = null;
                }
                else {
                    primaryTraveler.email = purchaseData.primaryTraveler.emailGroup.email;
                    request.policy.fulfillmentMethod = 'Email';
                }
            }

            primaryTraveler.address = this.mapAddress(purchaseData.primaryTraveler.address);

            request.travelers.push(primaryTraveler);
        }

        if (purchaseData.additionalTravelers) {
            purchaseData.additionalTravelers.forEach(t => {
                let additionalTraveler: ApiTraveler = this.mapTraveler(t);
                request.travelers.push(additionalTraveler);
            });
        }

        // Use primary traveler as policy buyer if
        if (purchaseData.purchaseForOther && purchaseData.policyBuyer) {
            request.policyBuyer = this.mapTraveler(purchaseData.policyBuyer);
            request.policyBuyer.address = this.mapAddress(purchaseData.policyBuyer.address);
        }
        else {
            request.policyBuyer = primaryTraveler;
        }

        // Customers loaded from the customer list are considered the primary traveler,  if
        // a customer was loaded and it is a purchase for other we are going to let Sales Force
        // reconcile the policy buyer
        if (purchaseData.customerId) {
            if (purchaseData.purchaseForOther) {
                request.policyBuyer.id = null;
            }
            primaryTraveler.id = purchaseData.customerId;
        }

        request.flights = [];

        // run eligibility on flights to determine # of connections for quoting
        let allFlightsHaveData: boolean = true;
        let purchaseDataFlightCount: number = 0;
        if (purchaseData.flights && purchaseData.flights.length > 0) {
            purchaseData.flights.forEach((flight: Flight) => {
                if (!flight.airline || !flight.flightNumber || !flight.flightDate) {
                    allFlightsHaveData = false;
                } else {
                    purchaseDataFlightCount = purchaseDataFlightCount + 1;
                }
            });
        }

        // if just quoting the number of flights, use blank
        // flights in the request
        if (flightCount && (!allFlightsHaveData || flightCount !== purchaseDataFlightCount)) {
            for (let i = 0; i < flightCount; i++) {
                request.flights.push(new ApiFlight());
            }
        }
        else if (purchaseData.flights) {
            purchaseData.flights.forEach((flight, index) => {
                let apiFlight: ApiFlight = Flight.toApiFlight(flight, index);
                if (purchaseData.acknowledgements) {
                    apiFlight.acknowledgements = AcknowledgementService.getAcknowledgementsForFlight(flight, purchaseData.acknowledgements);
                }

                request.flights.push(apiFlight);
            });
        }

        request.policy.checkFlightEligibility = allFlightsHaveData;

        // if not letting eligibility count the number of flights, use the UI calculated number of
        // connections based on previous quotes
        if (!request.policy.checkFlightEligibility) {
            const currentQuote: ApiQuoteResponse = this.getCurrentQuote();
            if (currentQuote && (currentQuote.flightConnectionCount || currentQuote.flightConnectionCount === 0)) {
                const numberOfFlights: number = request.flights ? request.flights.length : 0;

                // to avoid sync issues, use # of flights - 1 if UI connection count >= # of flights
                let flightConnectionCount: number = currentQuote.flightConnectionCount;
                if (numberOfFlights > 0 && currentQuote.flightConnectionCount >= numberOfFlights) {
                    flightConnectionCount = numberOfFlights - 1;
                }

                request.policy.flightConnectionCount = flightConnectionCount;
            }
        }

        if (purchaseData.acknowledgements) {
            request.policy.acknowledgements = purchaseData.acknowledgements;
        }

        request.policy.overrideRequiresFlights = purchaseData.overrideRequiresFlights;
        request.policy.futureFlightsExpected = purchaseData.futureFlightsExpected;

        request.policy.isQuickQuote = true;

        return request;
    }

    private mapTraveler(traveler: Traveler): ApiTraveler {
        let apiTraveler: ApiTraveler = new ApiTraveler();
        apiTraveler.firstName = traveler.firstName;
        apiTraveler.lastName = traveler.lastName;
        apiTraveler.birthDate = traveler.birthDate;
        apiTraveler.tripCost = traveler.tripCost;
        apiTraveler.phoneNumber = traveler.phoneNumber;
        apiTraveler.gender = traveler.gender ? traveler.gender : null;

        if (traveler.emailGroup && !traveler.emailGroup.noEmail) {
            apiTraveler.email = traveler.emailGroup.email;
            if (apiTraveler.email === '') {
                apiTraveler.email = null;
            }
        }

        return apiTraveler;
    }

    private mapAddress(address: Address): ApiAddress {
        let apiAddress: ApiAddress = new ApiAddress();
        if (address) {
            apiAddress.address1 = address.address1;
            apiAddress.address2 = address.address2;
            apiAddress.city = address.city;
            apiAddress.stateOrProvince = address.stateOrProvince;
            apiAddress.postalCode = address.postalCode;
        }
        return apiAddress;
    }

    private mapPaymentInformation(billingInformation: BillingInformation): ApiPaymentInformation {
        let paymentInfo: ApiPaymentInformation = new ApiPaymentInformation();

        paymentInfo.address = billingInformation.address1;
        paymentInfo.city = billingInformation.city;
        paymentInfo.customerId = billingInformation.customerId;
        paymentInfo.isTest = false;
        paymentInfo.merchantProfile = this.merchantProfile;
        paymentInfo.nameOnCard = billingInformation.name;
        paymentInfo.paymentMethod = this.paymentMethod;
        paymentInfo.state = billingInformation.stateOrProvince;
        paymentInfo.paymentToken = billingInformation.paymentToken;
        paymentInfo.zip = billingInformation.postalCode;

        return paymentInfo;
    }

    private mapPurchaseDataForTravelers(data: PurchaseData, quote: ApiQuoteTripResponse): void {
        data.destinationCountry = quote.destinationCountry;
        data.depositDate = quote.initialDepositDate;
        data.departureDate = (quote.departureDates && quote.departureDates.localized) ? quote.departureDates.localized.dateString : null;
        data.returnDate = (quote.returnDates && quote.returnDates) ? quote.returnDates.localized.dateString : null;
        data.additionalTravelers = [];

        for (let i = 0; i < quote.travelers.length; i++) {
            let apiTraveler: ApiTraveler = quote.travelers[i];
            let t: Traveler = Traveler.fromApiTraveler(apiTraveler);

            // assign primary traveler or add to additional travelers
            if (apiTraveler.isPrimary) {
                data.primaryTraveler = t;
                if (apiTraveler.address && apiTraveler.address.stateOrProvince) {
                    data.residenceState = apiTraveler.address.stateOrProvince;
                }
            } else {
                data.additionalTravelers.push(t);
            }
        }
    }

    private mapPurchaseCoverages(data: PurchaseData, quote: ApiQuoteTripResponse): void {
        data.rentalCarPickupDate = quote.rentalCarPickupDate;
        data.rentalCarReturnDate = quote.rentalCarReturnDate;

        // checks to make sure that there is a selected package and that the selected package has optional coverages before attempting to map coverages
        if (quote.selectedPackages && quote.selectedPackages.length > 0 && quote.selectedPackages[0].coverages && quote.selectedPackages[0].coverages.optional) {
            for (let i = 0; i < quote.selectedPackages[0].coverages.optional.length; i++) {
                let optionalCoverage: ApiCoverageDetail = quote.selectedPackages[0].coverages.optional[i];

                // only maps selected coverages
                if (optionalCoverage.isSelected) {
                    let c: Coverage = Coverage.fromApiCoverageDetail(optionalCoverage);

                    data.selectedCoverages.push(c);
                }
            }
        }
    }

    private mapPurchaseDataFlights(data: PurchaseData, quote: ApiQuoteTripResponse): void {
        data.flights = [];

        if (quote.flights) {
            for (let i = 0; i < quote.flights.length; i++) {
                let apiFlight: ApiFlight = !quote.flights[i].flightResponseData ? new ApiFlight() : quote.flights[i].flightResponseData[0];
                let f: Flight = Flight.fromApiFlight(apiFlight);
                data.flights.push(f);
            }
        }

        data.overrideRequiresFlights = quote.overrideRequiresFlights;
        data.futureFlightsExpected = quote.futureFlightsExpected;
    }

    private mapPurchaseDataForFinishQuote(data: PurchaseData, quote: ApiQuoteTripResponse): void {
        data.destinationState = (quote.destinationState) ? quote.destinationState.isoCode2 : null;
        data.destinationCity = quote.destinationCity;
        data.airlineNotProvided = quote.noAirline;
        data.airline = quote.airline;
        data.tourOperator = quote.tourOperator;
        data.carRentalCompany = quote.carRentalCompany;
        data.hotel = quote.hotel;
        data.cruiseLine = quote.cruiseLine;
        data.rentalCompany = quote.rentalCompany;
    }

    private checkFlightsForPurchase(request: ApiQuoteRequest): Array<ApiFlight> {
        // make sure flights have all data filled in so they can be saved
        if (request && request.flights) {
            let flights: ApiFlight[] = [];
            request.flights.forEach((f: ApiFlight) => {
                if (f.departureAirportCode && f.arrivalAirportCode && f.airlineCode &&
                    f.flightNumber && f.departureDate) {
                    flights.push(f);
                }
            });

            return flights;
        }

        return [];
    }

    private compareEditedQuote(currentQuote: ApiQuoteResponse): string {
        let message: string;
        const previousQuote: ApiQuoteResponse = this.getCurrentQuote();
        let diffCoverages: string[] = [];

        if (previousQuote && previousQuote.coverages) {
            diffCoverages.push(...this.getUnavailableCoveragesMessage(previousQuote.coverages.bonus, currentQuote.coverages.bonus));
            diffCoverages.push(...this.getUnavailableCoveragesMessage(previousQuote.coverages.optional, currentQuote.coverages.optional));

            const premiumMessage: string = currentQuote.baseQuoteAmount != previousQuote.baseQuoteAmount ? 'Premium and ' : '';
            const coverageMessage: string = diffCoverages.length > 0 ? diffCoverages.join(', ') : '';

            if (coverageMessage.length > 0) {
                message = `Something changed. ${premiumMessage}${coverageMessage} eligibility for the plan you previously quoted or purchased has changed. Please review your updated plan coverages carefully before continuing.`;
            }

            return message;
        }
    }

    private getUnavailableCoveragesMessage(previousCoverages: ApiCoverageDetail[], currentCoverages: ApiCoverageDetail[]): string[] {
        const removedCoverages: ApiCoverageDetail[] = [];
        const coverages: string[] = [];
        const addedCoverages: ApiCoverageDetail[] = [];

        if (currentCoverages) {
            currentCoverages.forEach(coverage => {
                if (!coverage.coverageAvailable) {
                    removedCoverages.push(coverage);
                }
                if (coverage.coverageAvailable) {
                    addedCoverages.push(coverage);
                }
            });
        }

        if (previousCoverages) {
            previousCoverages.forEach((coverage) => {
                if (removedCoverages.some(c => c.ratingId === coverage.ratingId)) {
                    coverages.push(coverage.name);
                }
            });
        }

        // If removed coverages are empty, check if any were automatically added back and add to list
        if (coverages.length === 0 && previousCoverages) {
            previousCoverages.forEach((coverage) => {
                if (!coverage.coverageAvailable && addedCoverages.some(c => c.ratingId === coverage.ratingId)) {
                    coverages.push(addedCoverages.find(c => c.ratingId === coverage.ratingId).name);
                }
            });
        }

        return coverages;
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/purchase/quoting.service.ts